/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import {
  Button,
  Checkbox,
  Divider,
  Flex,
  Text,
  TonicProps,
} from '@tonic-one/react';
import { read } from 'fs';
import { use } from 'i18next';
import cloneDeep from 'lodash/cloneDeep';

import React, { Key } from 'react';
import { useTranslation } from 'react-i18next';
import { useWhyDidYouUpdate } from 'src/utils/hooks';

import Tree from '../Tree';
import { FlexContainer, SearchInput } from './Components';
import { useCasTreeTransferContext } from './context';
import { searchTree, traverseTreeData } from './utils';

interface TransferRightProps extends TonicProps {
  title?: string;
  placeholder?: string;
  onImport?: (fn: Function) => void;
  onExport?: (entries: any) => void;
}
const RightBox: React.FC<TransferRightProps> = ({
  title,
  placeholder,
  onImport,
  onExport,
  ...tonicProps
}) => {
  const { t } = useTranslation();
  const {
    dataSourceRight,
    checkListRight,
    readOnlyAllKeyRight,
    readOnlyDataSourceRight,
    disabled,
    handleCheckListRightChange,
    handleDataSourceRightChange,
    handleIsBrightLeftChange,
    handleReadOnlyAllKeyRightChange,
    handleReadOnlyDataSourceRightChange,
  } = useCasTreeTransferContext();
  // console.log('dataSourceRight, checkListRight');
  // console.info(dataSourceRight, checkListRight);
  const [checkAll, setCheckAll] = React.useState<boolean>(false); // 控制全选
  const [indeterminateRight, setIndeterminate] = React.useState<boolean>(false); // 控制半选
  const [searchText, setSearchText] = React.useState<string>(''); // 用于搜索title
  const showCheckAll = React.useMemo(() => false, []);
  const [handleSearchTree, handleSearchChange] = React.useMemo(() => {
    const handleSearchTree = (value: string) => {
      const tempValue = value.trim();
      if (tempValue === '') {
        // 搜索框为空时，显示原始数据源
        //获取右侧原始数据源
        const treeData = cloneDeep(readOnlyDataSourceRight.treeData);
        // 保存右侧数据源
        handleDataSourceRightChange({ treeData });
      } else {
        handleDataSourceRightChange({
          treeData: searchTree(value, dataSourceRight.treeData),
        });
      }
    };
    const handleSearchChange = (value: string) => {
      setSearchText(value);
    };
    return [handleSearchTree, handleSearchChange];
  }, [searchText, dataSourceRight.treeData, readOnlyDataSourceRight.treeData]);
  const [handleCheckboxChange, handleSelectNode] = React.useMemo(() => {
    const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      // 设置全选
      setCheckAll(e.target.checked);
      // 设置半选
      setIndeterminate(false);
      // 设置按钮高亮
      handleIsBrightLeftChange(e.target.checked);
      // 保存右侧选中的key
      handleCheckListRightChange(e.target.checked ? readOnlyAllKeyRight : []);
    };
    const handleSelectNode = (checkedKeys: Key[]) => {
      // 保存右侧选中的key
      handleCheckListRightChange(checkedKeys);
      // 设置全选
      setCheckAll(
        checkedKeys.length > 0 &&
          checkedKeys.length === readOnlyAllKeyRight.length
      );
      // 设置半选
      setIndeterminate(
        checkedKeys.length > 0 &&
          checkedKeys.length < readOnlyAllKeyRight.length
      );
      // 设置按钮高亮
      handleIsBrightLeftChange(checkedKeys.length > 0);
    };
    return [handleCheckboxChange, handleSelectNode];
  }, [checkAll, readOnlyAllKeyRight]);
  const handleImportCallback = React.useCallback(
    (importData: React.Key[]) => {
      // 构建新的右侧数据源
      const newRightKeys = Array.from(
        new Set([...readOnlyAllKeyRight, ...importData])
      );
      // 保存右侧数据源
      const importTreeData = importData.map((item: any) => {
        return {
          key: item,
          title: item,
        };
      });
      const newDataSource = cloneDeep(readOnlyDataSourceRight.treeData);
      newDataSource[0].children = newDataSource[0].children
        ? newDataSource[0].children.concat(importTreeData)
        : importTreeData;
      handleDataSourceRightChange({ treeData: newDataSource });
      // 更新右侧只读key
      handleReadOnlyAllKeyRightChange(newRightKeys);
      // 更新右侧只读数据源
      handleReadOnlyDataSourceRightChange({ treeData: newDataSource });
    },
    [
      dataSourceRight.treeData,
      readOnlyDataSourceRight.treeData,
      readOnlyAllKeyRight,
    ]
  );
  React.useEffect(() => {
    if (checkListRight.length === 0) {
      setCheckAll(false);
      setIndeterminate(false);
    }
  }, [checkListRight]);
  useWhyDidYouUpdate('RightBox', {
    dataSourceRight,
    checkListRight,
    readOnlyAllKeyRight,
    readOnlyDataSourceRight,
    disabled,
    handleCheckListRightChange,
    handleDataSourceRightChange,
    handleIsBrightLeftChange,
    handleReadOnlyAllKeyRightChange,
    handleReadOnlyDataSourceRightChange,
    checkAll,
    indeterminateRight,
    searchText,
    showCheckAll,
  });
  return (
    <FlexContainer direction="column" width="520px" {...tonicProps}>
      {title && (
        <Flex mb="12px">
          <Text>{title}</Text>
        </Flex>
      )}
      {showCheckAll && (
        <Flex>
          <Checkbox
            onChange={handleCheckboxChange}
            checked={checkAll}
            indeterminate={indeterminateRight}
            disabled={disabled}
          />
          <Text ml="8px">{`${checkListRight.length} / ${readOnlyAllKeyRight.length} item selected`}</Text>
        </Flex>
      )}
      <Flex mb="12px">
        <SearchInput
          onChange={handleSearchChange}
          onSearch={handleSearchTree}
          value={searchText}
          // placeholder={placeholder || 'Search items'}
          placeholder={placeholder || t('policy.placeholder.policySearchItems')}
          autoClear
          width="100%"
          disabled={disabled}
        />
        {onImport && (
          <Button
            variant="secondary"
            width="66px"
            height="32px"
            ml="12px"
            onClick={() => onImport(handleImportCallback)}
            disabled={disabled}
          >
            Import
          </Button>
        )}
        {onExport && (
          <Button
            variant="secondary"
            width="66px"
            height="32px"
            ml="12px"
            onClick={() => {
              onExport(readOnlyAllKeyRight);
            }}
            disabled={disabled}
          >
            Export
          </Button>
        )}
      </Flex>
      <Divider mb="12px" />
      <Tree
        treeData={dataSourceRight.treeData}
        onCheck={handleSelectNode}
        checkedKeys={checkListRight as (string | number)[]}
        style={{ height: '284px' }}
      ></Tree>
    </FlexContainer>
  );
};

export default React.memo(RightBox);
